import {
    IReaderView,
    IReaderViewAnnotation,
    IReaderViewAnnotationEngineEvent,
    IReaderViewAnnotationLayer,
    IReaderViewAnnotationMouseEngineEvent,
    IReaderViewAnnotationPointerEngineEvent,
} from '@colibrio/colibrio-reader-framework/colibrio-readingsystem-base';
import {VanillaReaderAppUiDefaults} from '../VanillaReaderUI/VanillaReaderAppUiDefaults';
import {
    IVanillaReaderHighlightData,
    VanillaAnnotationMouseEventCallback,
    VanillaAnnotationPointerEventCallback,
    VanillaHighlightDataCallback,
    VanillaVoidCallback,
} from './VanillaReaderModel';

/**
 * # VanillaReaderAnnotationLayerHighlights
 *
 * ## PRIMARY COLIBRIO FRAMEWORK TYPES USED
 *
 * - IReaderViewAnnotationLayer
 * - IReaderViewAnnotation
 * - IReaderViewAnnotationEngineEvent
 *
 * ## RESPONSIBILITIES
 *
 * This class wraps the highlight's ReaderViewAnnotationLayer. It also acts as runtime storage for highlights.
 *
 */

export class VanillaReaderAnnotationLayerHighlights {
    private _locatorToAnnotationMap: Map<string, IReaderViewAnnotation<IVanillaReaderHighlightData>> = new Map<string, IReaderViewAnnotation>();
    private readonly _colibrioAnnotationLayer: IReaderViewAnnotationLayer;
    private readonly _highlightsInVisibleRange: Set<IVanillaReaderHighlightData>;
    private _highlightAddedCallback: VanillaHighlightDataCallback | undefined = undefined;
    private _highlightDeletedCallback: VanillaHighlightDataCallback | undefined = undefined;
    private _highlightUpdatedCallback: VanillaHighlightDataCallback | undefined = undefined;
    private _highlightsCollectionUpdatedCallback: VanillaVoidCallback | undefined = undefined;
    private _highlightClickCallback: VanillaAnnotationMouseEventCallback | undefined = undefined;
    private _highlightPointerEnterCallback: VanillaAnnotationPointerEventCallback | undefined = undefined;
    private _highlightPointerLeaveCallback: VanillaAnnotationPointerEventCallback | undefined = undefined;
    private _highlightEnterViewCallback: VanillaHighlightDataCallback | undefined = undefined;
    private _highlightExitViewCallback: VanillaHighlightDataCallback | undefined = undefined;
    private _highlightContextMenuCallback: VanillaAnnotationMouseEventCallback | undefined = undefined;

    constructor(
        private name: string,
        private color: string = VanillaReaderAppUiDefaults.highlightColorValues.yellow,
        private _colibrioReaderView: IReaderView,
    ) {
        this._colibrioAnnotationLayer = this._colibrioReaderView.createAnnotationLayer(this.name);
        this._highlightsInVisibleRange = new Set<IVanillaReaderHighlightData>();
        this._create();
    }

    onHighlightAdded(callback: VanillaHighlightDataCallback): void {
        this._highlightAddedCallback = callback;
    }

    onHighlightUpdated(callback: VanillaHighlightDataCallback): void {
        this._highlightUpdatedCallback = callback;
    }

    onHighlightDeleted(callback: VanillaHighlightDataCallback): void {
        this._highlightDeletedCallback = callback;
    }

    onHighlightsCollectionUpdated(callback: VanillaVoidCallback): void {
        this._highlightsCollectionUpdatedCallback = callback;
    }

    onHighlightClick(callback: VanillaAnnotationMouseEventCallback): void {
        this._highlightClickCallback = callback;
    }

    onHighlightPointerEnter(callback: VanillaAnnotationPointerEventCallback): void {
        this._highlightPointerEnterCallback = callback;
    }

    onHighlightPointerLeave(callback: VanillaAnnotationPointerEventCallback): void {
        this._highlightPointerLeaveCallback = callback;
    }

    onHighlightEnterView(callback: VanillaHighlightDataCallback): void {
        this._highlightEnterViewCallback = callback;
    }

    onHighlightExitView(callback: VanillaHighlightDataCallback): void {
        this._highlightExitViewCallback = callback;
    }

    onHighlightContextMenu(callback: VanillaAnnotationMouseEventCallback): void {
        this._highlightContextMenuCallback = callback;
    }

    loadHighlights(items: IVanillaReaderHighlightData[]): void {
        if (this._colibrioAnnotationLayer) {
            items.forEach((item) => {
                this.addHighlight(item);
            });
        }
    }

    getHighlights(): IVanillaReaderHighlightData[] {
        let highlights: IVanillaReaderHighlightData[] = [];
        this._colibrioAnnotationLayer.getAnnotations().forEach((item) => {
            highlights.push(item.getCustomData());
        });
        return highlights;
    }

    getReaderViewAnnotations(): IReaderViewAnnotation[] {
        return this._colibrioAnnotationLayer.getAnnotations();
    }

    addHighlight(highlightData: IVanillaReaderHighlightData): void {
        let readerViewAnnotation: IReaderViewAnnotation;

        readerViewAnnotation = this._colibrioAnnotationLayer.createAnnotation<IVanillaReaderHighlightData>(highlightData.locator);
        readerViewAnnotation.setOptions({
            rangeStyle: {
                'background-color': highlightData.color,
            },
        });
        readerViewAnnotation.setCustomData(highlightData);

        this._locatorToAnnotationMap.set(highlightData.locator, readerViewAnnotation);

        if (this._highlightAddedCallback) {
            this._highlightAddedCallback(highlightData);
        }

        if (this._highlightsCollectionUpdatedCallback) {
            this._highlightsCollectionUpdatedCallback();
        }

    }

    updateHighlight = (highlight: IVanillaReaderHighlightData) => {
        let existingTarget = this._locatorToAnnotationMap.get(highlight.locator);
        if (existingTarget) {
            existingTarget.setCustomData(highlight);
            existingTarget.setOptions({
                rangeStyle: {
                    'background-color': highlight.color,
                },
            });

            if (this._highlightUpdatedCallback) {
                this._highlightUpdatedCallback(highlight);
            }

            if (this._highlightsCollectionUpdatedCallback) {
                this._highlightsCollectionUpdatedCallback();
            }

        } else {
            console.log('annotationLayerHighlights_update', 'Could not find existing VanillaReaderHighlight with contentBlockLocator ', highlight.locator);
        }
    };

    deleteHighlight(locator: string): void {

        let existingTarget = this._locatorToAnnotationMap.get(locator);
        if (existingTarget) {
            this._locatorToAnnotationMap.delete(locator);
            let vanillaReaderAnnotationData = existingTarget.getCustomData();
            existingTarget.getAnnotationLayer().destroyAnnotation(existingTarget);

            if (this._highlightDeletedCallback) {
                if (vanillaReaderAnnotationData) {
                    this._highlightDeletedCallback(vanillaReaderAnnotationData);
                } else {
                    console.warn(`VanillaReaderAnnotationLayerHighlights.deleteHighlight(): Unable to get annotation data for annotation with locator "${locator}"`);
                }
            }

            if (this._highlightsCollectionUpdatedCallback) {
                this._highlightsCollectionUpdatedCallback();
            }

        } else {
            console.log('annotationLayerHighlights_deleteHighlight', 'Could not find existing VanillaReaderHighlight with contentBlockLocator ', locator);
        }

    }

    getHighlight(locator: string): IVanillaReaderHighlightData | undefined {
        if (this._locatorToAnnotationMap.has(locator)) {
            return this._locatorToAnnotationMap.get(locator)!.getCustomData() || undefined;
        } else {
            return;
        }
    }

    destroy(): void {
        this._colibrioReaderView.destroyAnnotationLayer(this._colibrioAnnotationLayer);
    }

    clear(): void {
        this._colibrioAnnotationLayer.destroyAllAnnotations();
    }

    getHighlightsInVisibleRange(): IVanillaReaderHighlightData[] {
        return Array.from(this._highlightsInVisibleRange);
    }

    /*
    *
    * PRIVATE METHODS
    *
    * */

    private _create(): void {
        this._colibrioAnnotationLayer.setDefaultAnnotationOptions({
            rangeClassName: 'vanilla-reader__highlights-layer',
            rangeStyle: {
                'background-color': this.color,
                'padding': '2px',
                'margin-top': '-2px',
                'margin-left': '-2px',
                'border-radius': '4px'
            },
        });
        this._colibrioAnnotationLayer.setLayerOptions({
            annotationInputEngineEventsEnabled: true,
            layerStyle: {'mix-blend-mode': 'multiply'},
        });

        this._colibrioAnnotationLayer.addEngineEventListener('annotationContextMenu', this._event_contextMenu);
        this._colibrioAnnotationLayer.addEngineEventListener('annotationClick', this._event_click);
        this._colibrioAnnotationLayer.addEngineEventListener('annotationIntersectingVisibleRange', this._event_enterView);
        this._colibrioAnnotationLayer.addEngineEventListener('annotationOutsideVisibleRange', this._event_exitView);
        this._colibrioAnnotationLayer.addEngineEventListener('annotationPointerEnter', this._event_pointerEnter);
        this._colibrioAnnotationLayer.addEngineEventListener('annotationPointerLeave', this._event_pointerLeave);
    }

    /*
    *
    * EVENT HANDLERS
    *
    * */

    private _event_click = (event: IReaderViewAnnotationMouseEngineEvent) => {
        if (this._highlightClickCallback) {
            this._highlightClickCallback(event);
        }
    };
    private _event_enterView = (event: IReaderViewAnnotationEngineEvent) => {
        this._highlightsInVisibleRange.add(event.annotation.getCustomData());
        if (this._highlightEnterViewCallback) {
            this._highlightEnterViewCallback(event.annotation.getCustomData());
        }
    };

    private _event_exitView = (event: IReaderViewAnnotationEngineEvent) => {
        this._highlightsInVisibleRange.delete(event.annotation.getCustomData());
        if (this._highlightExitViewCallback) {
            this._highlightExitViewCallback(event.annotation.getCustomData());
        }
    };
    private _event_pointerEnter = (event: IReaderViewAnnotationPointerEngineEvent) => {
        if (this._highlightPointerEnterCallback) {
            this._highlightPointerEnterCallback(event);
        }
    };
    private _event_pointerLeave = (event: IReaderViewAnnotationPointerEngineEvent) => {
        if (this._highlightPointerLeaveCallback) {
            this._highlightPointerLeaveCallback(event);
        }

    };
    private _event_contextMenu = (event: IReaderViewAnnotationMouseEngineEvent) => {

        if (this._highlightContextMenuCallback) {
            this._highlightContextMenuCallback(event);
        }

    };

}
